import React, { useState, useRef } from 'react';
import { X, UploadCloud, Download, FileText, Loader2 } from 'lucide-react';
import { parseCSV } from '../../utils/csv';

export default function CsvSetupModal({ isOpen, onClose, onStartImport, importType = 'leads' }) {
    const [file, setFile] = useState(null);
    const [rowCount, setRowCount] = useState(0);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const fileInputRef = useRef(null);

    if (!isOpen) return null;

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            analyzeFile(selectedFile);
        }
    };

    const analyzeFile = (file) => {
        setFile(file);
        setIsAnalyzing(true);
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                // simple quick analysis to count rows (excluding header)
                const text = evt.target.result;
                // using basic split for speed just to count
                const lines = text.split(/\r\n|\n/).filter(line => line.trim().length > 0);
                // Assume 1 header row
                const count = Math.max(0, lines.length - 1);
                setRowCount(count);
            } catch (err) {
                console.error(err);
            } finally {
                setIsAnalyzing(false);
            }
        };
        reader.readAsText(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile && droppedFile.name.endsWith('.csv')) {
            analyzeFile(droppedFile);
        }
    };

    const downloadTemplate = () => {
        let headers, filename;
        if (importType === 'contacts') {
            headers = ["First Name", "Last Name", "Phone", "Email", "Notes"];
            filename = "contacts_template.csv";
        } else {
            headers = ["Client Name", "Phone", "Email", "Amount", "Source", "Status", "Manager", "Date", "Event Date", "Event Type", "Guests", "Venue", "Notes"];
            filename = "leads_template.csv";
        }
        
        const csvContent = "data:text/csv;charset=utf-8," + headers.join(",");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 overflow-hidden animate-fade-in">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-slate-50">
                    <h3 className="font-bold text-gray-900 text-lg">{importType === 'contacts' ? 'Import Contacts' : 'Import Leads'}</h3>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-6 space-y-6">
                    {/* Template Download */}
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center justify-between">
                        <div>
                            <p className="text-sm font-semibold text-blue-900">Need a template?</p>
                            <p className="text-xs text-blue-700 mt-0.5">Download our sample CSV format</p>
                        </div>
                        <button 
                            onClick={downloadTemplate}
                            className="flex items-center gap-2 bg-white text-blue-600 border border-blue-200 hover:bg-blue-50 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shadow-sm"
                        >
                            <Download className="w-4 h-4" />
                            Template
                        </button>
                    </div>

                    {/* Upload Area */}
                    {!file ? (
                        <div 
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className="border-2 border-dashed border-gray-300 rounded-2xl p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-slate-50 hover:border-blue-400 transition-colors"
                        >
                            <div className="w-12 h-12 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mb-3">
                                <UploadCloud className="w-6 h-6" />
                            </div>
                            <p className="text-sm font-semibold text-gray-700">Click to upload or drag and drop</p>
                            <p className="text-xs text-gray-500 mt-1">CSV files only (Max 10MB)</p>
                            <input 
                                type="file" 
                                className="hidden" 
                                accept=".csv" 
                                ref={fileInputRef}
                                onChange={handleFileChange}
                            />
                        </div>
                    ) : (
                        <div className="border border-gray-200 rounded-2xl p-6 bg-slate-50 flex flex-col items-center justify-center text-center relative">
                            <button 
                                onClick={() => setFile(null)}
                                className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                            <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mb-3">
                                <FileText className="w-6 h-6" />
                            </div>
                            <p className="text-sm font-bold text-gray-800 truncate max-w-full px-4">{file.name}</p>
                            
                            {isAnalyzing ? (
                                <div className="flex items-center gap-2 mt-2 text-blue-600 text-xs font-medium">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Analyzing file...
                                </div>
                            ) : (
                                <div className="mt-2 bg-white border border-gray-200 px-3 py-1.5 rounded-full shadow-sm flex items-center gap-2 text-sm">
                                    <span className="text-gray-500">Total {importType === 'contacts' ? 'contacts' : 'leads'} found:</span>
                                    <span className="font-bold text-emerald-600">{rowCount}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => file && onStartImport(file)}
                        disabled={!file || isAnalyzing || rowCount === 0}
                        className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm flex items-center gap-2"
                    >
                        Start Import
                    </button>
                </div>
            </div>
        </div>
    );
}
